import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtBytes, clamp } from '@/utils/format';
import { useMemoryUsage, isMemoryApiAvailable } from '@/hooks/useMemoryUsage';

interface Props { autoRun: boolean; autoNonce: number; }

export function MemoryTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [leakMode, setLeakMode] = useState(false);
  const [chunkMB, setChunkMB] = useState(10);
  const [iterations, setIterations] = useState(14);
  const [intervalMs, setIntervalMs] = useState(250);
  const [metrics, setMetrics] = useState({
    created: 0,
    startHeap: null as number | null,
    currentHeap: null as number | null,
    peakHeap: null as number | null,
    status: 'idle',
  });

  const heap = useMemoryUsage(running);
  const keepRef = useRef<unknown[]>([]);
  const timerRef = useRef(0);
  const startHeapRef = useRef<number | null>(null);
  const autoDoneRef = useRef(false);

  useEffect(() => {
    if (!running) return;
    setMetrics((m) => {
      const peak = m.peakHeap == null || (heap != null && heap > (m.peakHeap ?? 0)) ? heap : m.peakHeap;
      return { ...m, currentHeap: heap, peakHeap: peak };
    });
  }, [heap, running]);

  function reset() {
    setRunning(false);
    clearInterval(timerRef.current);
    keepRef.current = [];
    autoDoneRef.current = false;
    startHeapRef.current = null;
    setMetrics({ created: 0, startHeap: null, currentHeap: null, peakHeap: null, status: 'idle' });
  }

  function doReport(endHeap: number | null) {
    const growth = startHeapRef.current != null && endHeap != null ? endHeap - startHeapRef.current : null;
    let sc = 60;
    if (growth != null) {
      sc = leakMode ? 45 : 75;
      if (!leakMode && growth < 80 * 1024 * 1024) sc = 90;
      if (!leakMode && growth > 200 * 1024 * 1024) sc = 55;
      if (leakMode && growth > 250 * 1024 * 1024) sc = 30;
    }
    const testId: TestId = 'tv-memory';
    reportResult(testId, {
      score: sc,
      primary: `${leakMode ? 'Leak' : 'Cycle'} · Peak ${fmtBytes(metrics.peakHeap)}`,
      details: { ...metrics, growthBytes: growth },
    });
    if (autoRun && !autoDoneRef.current) {
      autoDoneRef.current = true;
      onAutoDone(testId);
    }
  }

  function start() {
    reset();
    setRunning(true);
    const mb = clamp(parseInt(String(chunkMB), 10) || 10, 2, 30);
    const it = clamp(parseInt(String(iterations), 10) || 14, 3, 40);
    const iv = clamp(parseInt(String(intervalMs), 10) || 250, 80, 1500);
    setChunkMB(mb); setIterations(it); setIntervalMs(iv);
    startHeapRef.current = heap;
    setMetrics((m) => ({ ...m, status: 'running', startHeap: heap, currentHeap: heap, peakHeap: heap }));

    let i = 0;
    timerRef.current = window.setInterval(() => {
      i++;
      const bytes = mb * 1024 * 1024;
      const arr = new Uint8Array(bytes);
      for (let k = 0; k < bytes; k += 4096) arr[k] = (k + i) & 255;
      const graph: Record<string, unknown> = { t: Date.now(), a: arr, nodes: [] };
      const nodes = graph.nodes as unknown[];
      for (let n = 0; n < 2000; n++) nodes.push({ id: n, s: 'node-' + n, v: (n * i) % 9973 });

      if (leakMode) {
        keepRef.current.push(graph);
      } else {
        keepRef.current.push(graph);
        if (keepRef.current.length > 2) keepRef.current.shift();
      }

      setMetrics((m) => ({ ...m, created: i, status: i >= it ? 'done' : 'running' }));

      if (i >= it) {
        clearInterval(timerRef.current);
        setRunning(false);
        doReport(heap);
      }
    }, iv);
  }

  function stopAndScore() {
    setRunning(false);
    clearInterval(timerRef.current);
    doReport(heap);
  }

  useEffect(() => {
    if (!autoRun) return;
    setLeakMode(false);
    setChunkMB(8);
    setIterations(12);
    setIntervalMs(250);
    start();
  }, [autoRun, autoNonce]);

  const growth = metrics.startHeap != null && metrics.currentHeap != null ? metrics.currentHeap - metrics.startHeap : null;

  return (
    <TestCard title={t('tv.memory.title', language)} description={t('tv.memory.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stopAndScore} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.mode', language)}</div>
          <select value={leakMode ? 'leak' : 'cycle'} onChange={(e) => setLeakMode(e.target.value === 'leak')} disabled={running || autoRun} className="input">
            <option value="cycle">{t('opt.cycle', language)}</option>
            <option value="leak">{t('opt.leak', language)}</option>
          </select>
        </div>
        <div className="field">
          <div className="label">{t('label.chunkMB', language)}</div>
          <input value={chunkMB} disabled={running || autoRun} onChange={(e) => setChunkMB(Number(e.target.value))} inputMode="numeric" className="input w-20" />
        </div>
        <div className="field">
          <div className="label">{t('label.iterations', language)}</div>
          <input value={iterations} disabled={running || autoRun} onChange={(e) => setIterations(Number(e.target.value))} inputMode="numeric" className="input w-20" />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label="Heap API" value={isMemoryApiAvailable() ? t('supported.available', language) : t('supported.unavailable', language)} hint="Chrome-only" />
        <MetricBox label={t('metric.heapUsed', language)} value={fmtBytes(metrics.currentHeap)} />
        <MetricBox label={t('metric.peakHeap', language)} value={fmtBytes(metrics.peakHeap)} />
        <MetricBox label={t('metric.heapGrowth', language)} value={growth != null ? fmtBytes(growth) : '—'} />
      </div>
    </TestCard>
  );
}
